import { AppTableControls } from 'common/TableControls/TableControls';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import { AppButton } from 'shared/UI/UIKit/Button';
import { AppMultipleSelect, AppSelect } from 'shared/UI/UIKit/Select';
import { AppPlusIcon } from 'shared/UI/UIKit/icons/Plus';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import type { EducationClass } from 'services/api';
import type { Subject } from 'services/api/subjects';
import type { AppTableColumn } from 'shared/UI/UIKit/Table';
import type { TeacherWorkloadTableRow } from '../model/types';
import styles from '../ui/styles.module.scss';
import { getClassOptionsBySchoolGroup } from '../model/util';

type GetColumnsParams = {
  availableSubjects: Subject[];
  educationClasses: EducationClass[];
  subjectsLoading: boolean;
  tableDisabled: boolean;
  selectedSubjectIds: number[];
  onSubjectChange: (rowKey: string, subjectId: number) => void;
  onClassesChange: (rowKey: string, educationalClassIds: number[]) => void;
  onCopyRow: (rowKey: string) => void;
  onDeleteRow: (rowKey: string) => void;
  onAddSubjectRow: () => void;
};

export const ADD_SUBJECT_ROW_KEY = 'teacher-workload-add-subject-row';

export const TEACHER_WORKLOAD_ADD_SUBJECT_ROW: TeacherWorkloadTableRow = {
  key: ADD_SUBJECT_ROW_KEY,
  subjectId: null,
  workloadHours: 0,
  educationalClasses: [],
};

export const getColumns = ({
  availableSubjects,
  educationClasses,
  subjectsLoading,
  tableDisabled,
  selectedSubjectIds,
  onSubjectChange,
  onClassesChange,
  onCopyRow,
  onDeleteRow,
  onAddSubjectRow,
}: GetColumnsParams): AppTableColumn<TeacherWorkloadTableRow>[] => [
  {
    key: 'subjects',
    title: 'Предметы',
    dataIndex: ['subjectId'],
    minWidth: 300,
    sorter: false,
    className: styles.subjectColumn,
    render: (value: number | null, record: TeacherWorkloadTableRow) => {
      if (record.key === ADD_SUBJECT_ROW_KEY) {
        return (
          <AppButton
            type="text"
            htmlType="button"
            disabled={tableDisabled}
            onClick={onAddSubjectRow}
          >
            <AppFlex direction="row" alignItems="center" unit="4">
              <AppPlusIcon className={styles.plusIcon} />
              <span>Добавить</span>
            </AppFlex>
          </AppButton>
        );
      }
      return (
        <AppSelect
          className={styles.workloadTableSelect}
          value={value ?? undefined}
          loading={subjectsLoading}
          disabled={tableDisabled}
          showSearch
          optionFilterProp="label"
          placeholder="Выберите предмет"
          dropdownMatchSelectWidth={false}
          dropdownClassName={styles.subjectSelectDropdown}
          options={availableSubjects.map((subject) => ({
            value: subject.id,
            label: formatSubjectWithLevel(subject.name, subject.educationLevel),
            disabled:
              selectedSubjectIds.includes(subject.id) && (value == null || subject.id !== value),
          }))}
          onChange={(subjectId) => onSubjectChange(record.key, subjectId as number)}
        />
      );
    },
  },
  {
    key: 'educationalClasses',
    title: 'Классы',
    dataIndex: ['educationalClasses'],
    minWidth: 300,
    sorter: false,
    render: (_value, record: TeacherWorkloadTableRow) => {
      if (record.key === ADD_SUBJECT_ROW_KEY) {
        return null;
      }
      return (
        <AppMultipleSelect
          className={`${styles.workloadTableSelect} ${styles.classesColumn}`}
          value={record.educationalClasses}
          disabled={tableDisabled}
          options={getClassOptionsBySchoolGroup(educationClasses)}
          style={{ height: 'auto', minHeight: '4rem' }}
          onChange={(ids) => onClassesChange(record.key, ids as number[])}
        />
      );
    },
  },
  {
    key: 'controls',
    title: '',
    dataIndex: [],
    minWidth: 88,
    maxWidth: 88,
    fixed: 'right',
    render: (_: unknown, record: TeacherWorkloadTableRow) => {
      if (record.key === ADD_SUBJECT_ROW_KEY) {
        return null;
      }
      return (
        <AppTableControls
          copy={tableDisabled ? undefined : () => onCopyRow(record.key)}
          remove={() => onDeleteRow(record.key)}
        />
      );
    },
  },
];
